import { HttpErrorResponse } from '@angular/common/http';
import { ComponentType } from '@angular/cdk/portal';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { FeatureFlagsService } from '../../infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, filter, map, Observable, of, ReplaySubject, share, switchMap, take } from 'rxjs';
import { ActionAddOrRemoveOwnerComponent } from 'src/app/components/actions/action-add-or-remove-owner/action-add-or-remove-owner.component';
import { ActionDeleteComponent } from 'src/app/components/actions/action-delete/action-delete.component';
import {
  ActionSendEmailComponent,
  EmailData,
} from 'src/app/components/actions/action-send-email/action-send-email.component';
import { ActionSetAccessComponent } from 'src/app/components/actions/action-set-access/action-set-access.component';
import { ActionSetAccessEnhancedComponent } from 'src/app/components/actions/action-set-access-enhanced/action-set-access-enhanced.component';
import { ActionSetResourceQuarantineStateComponent } from 'src/app/components/actions/action-set-resource-quarantine-state/action-set-resource-quarantine-state.component';
import { GetFlowRunsActionComponent } from 'src/app/components/actions/get-flow-runs-action/get-flow-runs-action.component';
import {
  ActionSetCopilotAuthenticationDialogComponent,
  SetCopilotAuthenticationDialogResult,
} from 'src/app/components/actions/powerplatform-actions/set-copilot-authentication/action-set-copilot-authentication-dialog/action-set-copilot-authentication-dialog.component';
import {
  ConfirmationDialogComponent,
  ConfirmData,
} from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import {
  GenericRadioOptionDialogComponent,
  GenericRadioOptionData,
  GenericRadioOptionResult,
} from 'src/app/components/generic-radio-option-dialog/generic-radio-option-dialog.component';
import { GetResourceDefinitionActionComponent } from 'src/app/components/get-resource-definition-action/get-resource-definition-action.component';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { Label } from 'src/app/labels/englishLabels';
import { Platform, ResourceType } from 'src/app/models/api';
import { DownloadAttachmentActionOutputs } from 'src/app/models/api/actions/downloadAttachment.model';
import { getIntegrationStatusLabel } from 'src/app/models/api/integration.model';
import {
  Attachment,
  AttachmentType,
  InventoryEntityField,
  InventoryResourceKnownRelation,
  ResourceStatus,
} from 'src/app/models/api/inventoryResource.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import {
  ActionDeleteResponseModel,
  ActionResultCode,
  ActionsHttpServiceService,
  AddOrRemoveOwnerParamsModel,
  AutomationActivationActions,
  AutomationActivationParams,
  PowerplatformAgentActionAuthenticationMode,
  PowerplatformAgentActionConfirmationMode,
  RequiredParamsForActionsHttpRequests,
  RequiredParamsForChatGptEnterpriseDeleteConversationAction,
  RequiredParamsForViolationActionsHttpRequests,
  ResourceActionOutput,
  SetApplicationQuarantineStateParamsModel,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { HttpErrorHandlerService } from '../../httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from '../../infra/notifications/notifications.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { StoreService } from '../../infra/store/store.service';
import { ActionWithConfirmationTextComponent } from '../../../components/actions/action-with-confirmation-text/action-with-confirmation-text.component';
import { ActionDownloadComponent } from '../../../components/actions/action-download/action-download.component';
import { SendToSocDialogComponent } from '../../../components/send-to-soc-dialog/send-to-soc-dialog.component';
import { ViolationModel } from '../../../models/api/violations.model';

export interface EmailPlaceholder {
  label: string;
  description: string;
}

export interface OptionalParamsForActions {
  platformUrl?: string | null;
  platform?: Platform;
  resourceType?: ResourceType;
}

export interface ActionWithAutonomousTriggersParams extends RequiredParamsForActionsHttpRequests {
  ignoreIfAutonomousWithTriggers?: boolean;
}

export interface GetDefinitionEnabledResponse {
  enabled: boolean;
  disabledReason?: string;
}

export interface ParamsForActions<requiredParamsType extends RequiredParamsForActionsHttpRequests> {
  requiredParams: requiredParamsType;
  optionalParams: OptionalParamsForActions;
}

@Injectable({
  providedIn: 'root',
})
export class CommonActionsService {
  private readonly _matDialog = inject(MatDialog);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _actionsHttpService = inject(ActionsHttpServiceService);
  private readonly _httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly _notificationService = inject(NotificationsService);
  private readonly _displayCamelCaseStringPipe = inject(DisplayCamelCaseStringPipe);
  private readonly _storeService = inject(StoreService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  public get supportedSetAccessResourceTypesArray(): ResourceType[] {
    return [ResourceType.flow, ResourceType.canvas_app];
  }

  public get supportedAddOwnersResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment, ResourceType.custom_connector];
  }

  public get supportedRemoveOwnersResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment];
  }

  public get supportedDeletionResourceTypesArray(): ResourceType[] {
    return [ResourceType.environment, ResourceType.flow, ResourceType.canvas_app, ResourceType.connection];
  }

  public get supportedGetResourceDefinitionTypesArray(): ResourceType[] {
    return [ResourceType.flow];
  }

  private _openDialog<T, R = boolean>(component: ComponentType<unknown>, config: MatDialogConfig<T>): Observable<R> {
    return new Observable<R>((observer) => {
      const dialogRef = this._matDialog.open(component, config);

      const subscription = dialogRef.afterClosed().subscribe({
        next: (result: R) => {
          observer.next(result);
          observer.complete();
        },
        error: (err) => observer.error(err),
      });

      return () => {
        subscription.unsubscribe();
        if (dialogRef.getState() === 0) {
          // MatDialogState.OPEN = 0
          dialogRef.close();
        }
      };
    });
  }

  private _openConfirmationDialog(
    title: string,
    content: string,
    positiveText: string = Label.get('common.confirm'),
    negativeText: string = Label.get('common.cancel')
  ): Observable<boolean> {
    const confirmData: ConfirmData = {
      title,
      content,
      positiveOptionText: positiveText,
      negativeOptionText: negativeText,
    };

    return this._openDialog<ConfirmData, boolean>(ConfirmationDialogComponent, {
      data: confirmData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    });
  }

  private _handleHttpError(error: HttpErrorResponse, errorPrefix: string, defaultReason: string): Observable<never> {
    if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
      this._httpErrorHandlerService.handle(error, true);
    } else {
      let reason = defaultReason;
      if (typeof error.error?.detail === 'string') {
        reason = error.error.detail;
      }
      this._notificationService.error(`${errorPrefix}, reason: ${reason}`);
    }
    return EMPTY;
  }

  private _handleConfirmAction<T>(
    confirmTitle: string,
    confirmContent: string,
    apiCall: () => Observable<T>,
    successMessage: string,
    errorPrefix: string
  ): ReplaySubject<boolean> {
    const result = new ReplaySubject<boolean>(1);

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() =>
          apiCall().pipe(
            map(() => {
              this._notificationService.success(successMessage);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(error, errorPrefix, "Error without 'detail' field");
              return of(false);
            })
          )
        ),
        // If confirmation was denied (filter removed it), return false
        catchError(() => of(false))
      )
      .subscribe(result);

    return result;
  }

  public showRawData(actionsHttpParams: RequiredParamsForActionsHttpRequests): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.getResourceDefinitionAction.sensitiveDataDisclaimerPopupTitle');
    const confirmContent = Label.get('commonActions.getResourceDefinitionAction.sensitiveDataDisclaimer');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          return this._openDialog(GetResourceDefinitionActionComponent, {
            disableClose: false,
            panelClass: 'sidepanel-dialog-container',
            width: '700px',
            autoFocus: false,
            data: actionsHttpParams,
          }).pipe(map((actionSucceeded) => !!actionSucceeded));
        })
      )
      .subscribe(success);

    return success;
  }

  public OpenShowFlowRunsConfirmationDialogDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.getFlowRunsAction.confirmationPopupTitle');
    const confirmContent = Label.get('commonActions.getFlowRunsAction.confirmationPopupContent');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          return this._openDialog(GetFlowRunsActionComponent, {
            disableClose: false,
            panelClass: 'sidepanel-dialog-container',
            width: '700px',
            autoFocus: false,
            data: actionsHttpParams,
          }).pipe(map((actionSucceeded) => !!actionSucceeded));
        })
      )
      .subscribe(success);

    return success;
  }

  public addOrRemoveOwnerAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    isAddOwner: boolean,
    optionalParams: OptionalParamsForActions = {}
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    actionsHttpParams.isAdminEndpoint =
      this.getPermissionRequiredForResourceAction(actionsHttpParams.resourceType) == PermissionEnum.admin;
    const actionRequiredParams: AddOrRemoveOwnerParamsModel = {
      isAddOwner: isAddOwner,
      ...actionsHttpParams,
    };

    this._openDialog(ActionAddOrRemoveOwnerComponent, {
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
      width: '350px',
      autoFocus: false,
      data: {
        requiredParams: actionRequiredParams,
        optionalParams: optionalParams,
      } as ParamsForActions<AddOrRemoveOwnerParamsModel>,
    }).subscribe(success);

    return success;
  }

  public setAccessAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    optionalParams: OptionalParamsForActions = {}
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    // Check if this is a Fabric resource
    const isFabricResource = optionalParams.platform === Platform.powerBI;

    // Choose the appropriate component and configuration
    const componentToOpen = isFabricResource ? ActionSetAccessEnhancedComponent : ActionSetAccessComponent;
    const dialogConfig = {
      disableClose: isFabricResource,
      panelClass: 'sidepanel-dialog-container',
      width: '500px',
      autoFocus: false,
      data: {
        requiredParams: actionsHttpParams,
        optionalParams: optionalParams,
      } as ParamsForActions<RequiredParamsForActionsHttpRequests>,
    };

    this._openDialog(componentToOpen, dialogConfig).subscribe(success);

    return success;
  }

  public getResourceAttachment(
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    attachmentId: string
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._actionsHttpService
      .downloadAttachment(actionsHttpParams.integrationId, actionsHttpParams.resourceId, attachmentId)
      .pipe(
        map((response: DownloadAttachmentActionOutputs) => {
          window.open(response.downloadUrl);
          return true;
        }),
        catchError((error) => {
          this._httpErrorHandlerService.handle(error);
          return of(false);
        })
      )
      .subscribe(success);

    return success;
  }

  public getResourceMakerEmails(
    ownedBy: InventoryEntityField<InventoryResourceKnownRelation[]> | undefined,
    createdBy: InventoryEntityField<InventoryResourceKnownRelation> | undefined
  ): Set<string> {
    let ownerEmails: string[];
    if (ownedBy?.value) {
      ownerEmails = ownedBy.value.map((owner) => owner.email?.value).filter((email): email is string => email !== null);
    } else {
      ownerEmails = [];
    }
    const creatorEmail = createdBy?.value?.email?.value;
    if (ownerEmails.length > 0 && ownerEmails.every((email) => email !== null)) {
      return new Set(ownerEmails);
    }
    if (creatorEmail) {
      return new Set([creatorEmail]);
    }
    return new Set();
  }

  public powerplatformSetCopilotAuthenticationAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    // Create an error handler specific to this operation
    const handleSetAuthError = (error: HttpErrorResponse): Observable<boolean> => {
      if (error.status === 422 && error.error && error.error.title == 'Platform API invalid input') {
        this._notificationService.info(
          "Enforcing 'user sign-in' will work only if Copilot is already using manual authentication"
        );
      } else if (error.status === 422) {
        this._notificationService.error('Failed to set authentication. ' + (error.error?.detail || ''));
      } else {
        this._httpErrorHandlerService.handle(error);
      }
      return of(false);
    };

    this._openDialog<{ integrationId: string; resourceId: string }, SetCopilotAuthenticationDialogResult>(
      ActionSetCopilotAuthenticationDialogComponent,
      {
        disableClose: false,
        minWidth: '600px',
        panelClass: 'sidepanel-dialog-container',
        data: {
          integrationId: actionsHttpParams.integrationId,
          resourceId: actionsHttpParams.resourceId,
        },
      }
    )
      .pipe(
        switchMap((result) => {
          if (!result || result.type === null) {
            return of(false);
          }

          const type = result.type;
          let action = 'save';
          if (actionsHttpParams.resourceStatus && actionsHttpParams.resourceStatus === ResourceStatus.active) {
            action = 'saveAndPublish';
          }

          const successfullyText = 'Copilot authentication updated successfully';

          if (action === 'save') {
            return this._actionsHttpService
              .powerplatformSetCopilotAuthentication(
                actionsHttpParams.integrationId,
                actionsHttpParams.resourceId,
                type
              )
              .pipe(
                map(() => {
                  this._notificationService.success(successfullyText);
                  return true;
                }),
                catchError(handleSetAuthError)
              );
          } else {
            // saveAndPublish
            return this._actionsHttpService
              .powerplatformSetCopilotAuthentication(
                actionsHttpParams.integrationId,
                actionsHttpParams.resourceId,
                type
              )
              .pipe(
                switchMap(() =>
                  this._actionsHttpService
                    .powerplatformPublishCopilot(actionsHttpParams.integrationId, actionsHttpParams.resourceId)
                    .pipe(
                      map(() => {
                        this._notificationService.success(successfullyText);
                        return true;
                      }),
                      catchError(() => {
                        this._notificationService.error(
                          'Copilot authentication saved successfully but failed to publish'
                        );
                        return of(false);
                      })
                    )
                ),
                catchError(handleSetAuthError)
              );
          }
        })
      )
      .subscribe(success);

    return success;
  }

  public deleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionDeleteComponent, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: `${this._displayCamelCaseStringPipe.transform(actionsHttpParams.resourceType)} - ${resourceName}`,
      },
    })
      .pipe(
        switchMap((toDelete) => {
          if (!toDelete) {
            return of(false);
          }

          return this._actionsHttpService.deleteResource(actionsHttpParams).pipe(
            map((response: ActionDeleteResponseModel) => {
              if (response.alreadyDeleted) {
                this._notificationService.error('Resource was already deleted');
                return false;
              } else {
                this._notificationService.success('Resource was deleted successfully');
                return true;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
                this._httpErrorHandlerService.handle(error);
              } else {
                let text = "Error without 'detail' field";
                if (typeof error.error?.detail === 'string') {
                  text = error.error.detail;
                }
                this._notificationService.error(`Resource could not be deleted, reason: ${text}`);
              }
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public sendEmailAction(emailData: EmailData): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionSendEmailComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
      width: '800px',
      height: '605px',
      autoFocus: false,
      data: emailData,
    }).subscribe(success);

    return success;
  }

  public sendToSocAction(violation: ViolationModel): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(SendToSocDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
      width: '600px',
      height: 'auto',
      autoFocus: false,
      data: {
        findingId: violation.id,
        integrationId: violation.integration.id,
      },
    }).subscribe(success);

    return success;
  }

  public secureFlowSteps(params: RequiredParamsForViolationActionsHttpRequests): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const confirmTitle = Label.get('commonActions.manageSecureFlowStepsSolutionAwareAction.title');
    const confirmContent = Label.get('commonActions.manageSecureFlowStepsSolutionAwareAction.content');

    this._openConfirmationDialog(confirmTitle, confirmContent)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(false);
          }

          const combineNotificationText = (title: string, description: string | null) =>
            description ? `${title}: ${description}` : title;

          return this._actionsHttpService.secureFlowSteps(params).pipe(
            map((response: ResourceActionOutput) => {
              if (response.resultCode === ActionResultCode.failure) {
                this._notificationService.error(combineNotificationText(response.title, response.description));
                return false;
              } else if (response.resultCode === ActionResultCode.no_effect) {
                this._notificationService.info(combineNotificationText(response.title, response.description));
                return true;
              } else {
                this._notificationService.success(combineNotificationText(response.title, response.description));
                return true;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(error, 'Action execution failed', 'Error in Powerplatform API');
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public powerplatformAgentActionSetActionStatusDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): Observable<boolean> {
    const dialogData: GenericRadioOptionData = {
      headerTitle: Label.get('commonActions.actionStatus.label'),
      bodyTitle: Label.get('commonActions.actionStatus.description'),
      options: [
        {
          id: 'enabled',
          title: Label.get('commonActions.actionStatus.enableLabel'),
          description: Label.get('commonActions.actionStatus.enableDescription'),
        },
        {
          id: 'disabled',
          title: Label.get('commonActions.actionStatus.disableLabel'),
          description: Label.get('commonActions.actionStatus.disableDescription'),
        },
      ],
      applyButtonText: 'Apply',
      onApply: (selectedOptionId: string) => {
        let isEnabled: boolean;

        if (selectedOptionId === 'enabled') {
          isEnabled = true;
        } else if (selectedOptionId === 'disabled') {
          isEnabled = false;
        } else {
          this._notificationService.error('Invalid action status selected');
          return of(false);
        }

        const httpCall = isEnabled
          ? this._actionsHttpService.agentActionEnableAction(actionsHttpParams)
          : this._actionsHttpService.agentActionDisableAction(actionsHttpParams);

        return httpCall.pipe(
          map(() => {
            this._notificationService.success('Resource was updated successfully');
            return true;
          }),
          catchError(() => {
            this._notificationService.error('Resource could not be updated');
            return of(false);
          })
        );
      },
    };

    return this._openDialog<GenericRadioOptionData, GenericRadioOptionResult>(GenericRadioOptionDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    }).pipe(
      take(1),
      map(() => true),
      takeUntilDestroyed(this._destroyRef),
      share()
    );
  }

  public powerplatformAgentActionSetAuthenticationModeDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): Observable<boolean> {
    const dialogData: GenericRadioOptionData = {
      headerTitle: Label.get('commonActions.authenticationMode.label'),
      bodyTitle: Label.get('commonActions.authenticationMode.description'),
      options: [
        {
          id: 'invoker',
          title: Label.get('commonActions.authenticationMode.userContextTitle'),
          description: Label.get('commonActions.authenticationMode.userContextDescription'),
        },
        {
          id: 'maker',
          title: Label.get('commonActions.authenticationMode.authorContextTitle'),
          description: Label.get('commonActions.authenticationMode.authorContextDescription'),
        },
      ],
      applyButtonText: 'Apply',
      onApply: (selectedOptionId: string): Observable<boolean> => {
        let authMode: PowerplatformAgentActionAuthenticationMode;

        if (selectedOptionId === 'maker') {
          authMode = PowerplatformAgentActionAuthenticationMode.maker;
        } else if (selectedOptionId === 'invoker') {
          authMode = PowerplatformAgentActionAuthenticationMode.invoker;
        } else {
          this._notificationService.error('Invalid authentication mode selected');
          return of(false);
        }

        return this._actionsHttpService
          .agentActionUpdateAuthenticationMode(
            actionsHttpParams,
            authMode,
            (actionsHttpParams as ActionWithAutonomousTriggersParams).ignoreIfAutonomousWithTriggers
          )
          .pipe(
            map(() => {
              this._notificationService.success('Resource was updated successfully');
              return true;
            }),
            catchError(() => {
              this._notificationService.error('Resource could not be updated');
              return of(false);
            })
          );
      },
    };

    return this._openDialog<GenericRadioOptionData, GenericRadioOptionResult>(GenericRadioOptionDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    }).pipe(
      take(1),
      map(() => true),
      takeUntilDestroyed(this._destroyRef),
      share()
    );
  }

  public powerplatformAgentActionSetConfirmationModeDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): Observable<boolean> {
    const dialogData: GenericRadioOptionData = {
      headerTitle: Label.get('commonActions.confirmationMode.label'),
      bodyTitle: Label.get('commonActions.confirmationMode.description'),
      options: [
        {
          id: 'strict',
          title: Label.get('commonActions.confirmationMode.strictModeTitle'),
          description: Label.get('commonActions.confirmationMode.strictModeDescription'),
        },
        {
          id: 'disabled',
          title: Label.get('commonActions.confirmationMode.disabledModeTitle'),
          description: Label.get('commonActions.confirmationMode.disabledModeDescription'),
        },
      ],
      applyButtonText: 'Apply',
      onApply: (selectedOptionId: string) => {
        let confirmationMode: PowerplatformAgentActionConfirmationMode;

        if (selectedOptionId === 'strict') {
          confirmationMode = PowerplatformAgentActionConfirmationMode.strict;
        } else if (selectedOptionId === 'disabled') {
          confirmationMode = PowerplatformAgentActionConfirmationMode.disabled;
        } else {
          this._notificationService.error('Invalid confirmation mode selected');
          return of(false);
        }

        return this._actionsHttpService
          .agentActionUpdateConfirmationMode(
            actionsHttpParams,
            confirmationMode,
            (actionsHttpParams as ActionWithAutonomousTriggersParams).ignoreIfAutonomousWithTriggers
          )
          .pipe(
            map(() => {
              this._notificationService.success('Resource was updated successfully');
              return true;
            }),
            catchError(() => {
              this._notificationService.error('Resource could not be updated');
              return of(false);
            })
          );
      },
    };

    return this._openDialog<GenericRadioOptionData, GenericRadioOptionResult>(GenericRadioOptionDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    }).pipe(
      take(1),
      map(() => true),
      takeUntilDestroyed(this._destroyRef),
      share()
    );
  }

  public powerplatformAgentActionSetWebBrowsingDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): Observable<boolean> {
    const dialogData: GenericRadioOptionData = {
      headerTitle: Label.get('commonActions.webBrowsing.label'),
      bodyTitle: Label.get('commonActions.webBrowsing.description'),
      options: [
        {
          id: 'enable',
          title: Label.get('commonActions.webBrowsing.enableLabel'),
          description: Label.get('commonActions.webBrowsing.enableDescription'),
        },
        {
          id: 'disable',
          title: Label.get('commonActions.webBrowsing.disableLabel'),
          description: Label.get('commonActions.webBrowsing.disableDescription'),
        },
      ],
      applyButtonText: 'Apply',
      onApply: (selectedOptionId: string) => {
        let webBrowsingEnabled: boolean;

        if (selectedOptionId === 'enable') {
          webBrowsingEnabled = true;
        } else if (selectedOptionId === 'disable') {
          webBrowsingEnabled = false;
        } else {
          this._notificationService.error('Invalid web browsing option selected');
          return of(false);
        }

        return this._actionsHttpService.agentActionUpdateWebBrowsing(actionsHttpParams, webBrowsingEnabled).pipe(
          map(() => {
            this._notificationService.success('Resource was updated successfully');
            return true;
          }),
          catchError(() => {
            this._notificationService.error('Resource could not be updated');
            return of(false);
          })
        );
      },
    };

    return this._openDialog<GenericRadioOptionData, GenericRadioOptionResult>(GenericRadioOptionDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    }).pipe(
      take(1),
      map(() => true),
      takeUntilDestroyed(this._destroyRef),
      share()
    );
  }

  public powerplatformEnvironmentSetAuditDialog(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): Observable<boolean> {
    const dialogData: GenericRadioOptionData = {
      headerTitle: Label.get('commonActions.environmentAudit.label'),
      bodyTitle: Label.get('commonActions.environmentAudit.description'),
      options: [
        {
          id: 'enable',
          title: Label.get('commonActions.environmentAudit.enableLabel'),
          description: Label.get('commonActions.environmentAudit.enableDescription'),
        },
        {
          id: 'disable',
          title: Label.get('commonActions.environmentAudit.disableLabel'),
          description: Label.get('commonActions.environmentAudit.disableDescription'),
        },
      ],
      applyButtonText: 'Apply',
      onApply: (selectedOptionId: string): Observable<boolean> => {
        let isAuditEnabled: boolean;

        if (selectedOptionId === 'enable') {
          isAuditEnabled = true;
        } else if (selectedOptionId === 'disable') {
          isAuditEnabled = false;
        } else {
          this._notificationService.error('Invalid environment audit option selected');
          return of(false);
        }

        return this._actionsHttpService.environmentSetAudit(actionsHttpParams, isAuditEnabled).pipe(
          map(() => {
            this._notificationService.success('Environment audit setting was updated successfully');
            return true;
          }),
          catchError(() => {
            this._notificationService.error('Environment audit setting could not be updated');
            return of(false);
          })
        );
      },
    };

    return this._openDialog<GenericRadioOptionData, GenericRadioOptionResult>(GenericRadioOptionDialogComponent, {
      data: dialogData,
      disableClose: false,
      panelClass: 'sidepanel-dialog-container',
    }).pipe(
      take(1),
      map(() => true),
      takeUntilDestroyed(this._destroyRef),
      share()
    );
  }

  public powerplatformAgentActionDeleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `Agent Action - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.agentActionDeleteAction(actionsHttpParams),
      title
    );
  }

  public powerplatformAgentDeleteAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `Agent - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.agentDeleteAction(actionsHttpParams),
      title,
      'Deletion in progress, this may take a few seconds',
      'This action will delete agent and all its dependent components (e.g. actions, topics, etc.)'
    );
  }

  private _deleteResourceWithConfirmation(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    deleteAction: () => Observable<unknown>,
    title: string | undefined = undefined,
    inProgressMessage: string | undefined = undefined,
    extraDisclaimer: string | undefined = undefined
  ): ReplaySubject<boolean> {
    return this._actionWithConfirmation(
      resourceName,
      'Delete',
      'Could not delete resource',
      'Resource was deleted successfully',
      'Permanent delete',
      actionsHttpParams,
      ActionDeleteComponent,
      () => deleteAction(),
      title,
      inProgressMessage,
      extraDisclaimer
    );
  }

  /**
   * Common method for deletion actions that use the ActionDeleteComponent
   */
  private _actionWithConfirmation<TAction extends ComponentType<unknown> = typeof ActionWithConfirmationTextComponent>(
    resourceName: string,
    actionName: string,
    errorMessage: string,
    successMessage: string,
    confirmationText: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests,
    actionType: TAction,
    action: () => Observable<unknown>,
    title: string | undefined = undefined,
    inProgressMessage: string | undefined = undefined,
    extraDisclaimer: string | undefined = undefined
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);
    const titleToShow =
      title || `${this._displayCamelCaseStringPipe.transform(actionsHttpParams.resourceType)} - ${resourceName}`;
    this._openDialog(actionType, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: titleToShow,
        confirmationText: confirmationText,
        extraDisclaimer,
      },
    })
      .pipe(
        switchMap((toDelete) => {
          if (!toDelete) {
            return of(false);
          }

          if (inProgressMessage) {
            this._notificationService.info(inProgressMessage);
          }

          return action().pipe(
            map(() => {
              this._notificationService.success(successMessage);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              if (this._httpErrorHandlerService.isUnhandledExceptionError(error.status)) {
                this._httpErrorHandlerService.handle(error);
              } else {
                let text = "Error without 'detail' field";
                if (typeof error.error?.detail === 'string') {
                  text = error.error.detail;
                }
                // Handle specific error for resource not found = PLATFORM_API_FAILURE_RESOURCE_NOT_FOUND
                // Currently this is a "magic string" because our internal API does not return the errorCode as an int but as free text (string)
                if (error.error.errorCode === 'PLATFORM_API_FAILURE_RESOURCE_NOT_FOUND') {
                  this._notificationService.error(
                    `Error: The resource you are trying to ${actionName.toLowerCase()} no longer exists.`
                  );
                  return of(false);
                } else {
                  this._notificationService.error(`${errorMessage}, reason: ${text}`);
                }
              }
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public powerplatformAgentActionEnforceUserAuthenticationAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enforceUserAuthenticationAction.title');
    const confirmContent = Label.get('commonActions.enforceUserAuthenticationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnforceUserAuthenticationAction(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public powerplatformAgentActionEnforceUserConfirmationAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.enforceUserConfirmationAction.title');
    const confirmContent = Label.get('commonActions.enforceUserConfirmationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.agentActionEnforceUserConfirmationAction(actionsHttpParams),
      'Resource was updated successfully',
      'Resource could not be updated'
    );
  }

  public chatGptEnterpriseDeleteGptAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptDeleteAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseDeleteUserCanvasAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptUserCanvasDeleteAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseGetUserCanvasAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `GPT - ${resourceName}`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptGetUserCanvasAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseIsolateGptAction(
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.isolateGptAction.title');
    const confirmContent = Label.get('commonActions.isolateGptAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.gptIsolateAction(actionsHttpParams),
      'Resource was isolated successfully',
      'Resource could not be isolated'
    );
  }

  public chatGptEnterpriseDeleteGptKnowledgeAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    const title = `${resourceName} from the GPT workspace`;
    return this._deleteResourceWithConfirmation(
      resourceName,
      actionsHttpParams,
      () => this._actionsHttpService.gptDeleteGptKnowledgeAction(actionsHttpParams),
      title
    );
  }

  public chatGptEnterpriseGetGptKnowledgeFileUrlAction(
    resourceName: string,
    actionsHttpParams: RequiredParamsForActionsHttpRequests
  ): ReplaySubject<boolean> {
    return this._actionWithConfirmation(
      resourceName,
      'Download',
      Label.get('commonActions.download.errorMessage'),
      Label.get('commonActions.download.successMessage'),
      'Confirm download',
      actionsHttpParams,
      ActionDownloadComponent,
      () => this._actionsHttpService.gptGetKnowledgeFileUrlAction(actionsHttpParams)
    );
  }

  public chatGptEnterpriseDeleteGptConversationAction(
    actionsHttpParams: RequiredParamsForChatGptEnterpriseDeleteConversationAction
  ): ReplaySubject<boolean> {
    const confirmTitle = Label.get('commonActions.deleteGptConversationAction.title');
    const confirmContent = Label.get('commonActions.deleteGptConversationAction.content');

    return this._handleConfirmAction(
      confirmTitle,
      confirmContent,
      () => this._actionsHttpService.gptDeleteConversationAction(actionsHttpParams),
      'Conversation was deleted successfully',
      'Conversation could not be deleted'
    );
  }

  public setResourceQuarantineStateAction(
    resourceName: string,
    actionsHttpParams: SetApplicationQuarantineStateParamsModel
  ): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    this._openDialog(ActionSetResourceQuarantineStateComponent, {
      disableClose: false,
      minWidth: '600px',
      panelClass: 'sidepanel-dialog-container',
      data: {
        itemName: resourceName,
        quarantineStatus: actionsHttpParams.quarantine_status,
      },
    })
      .pipe(
        switchMap((shouldSetQuarantineState) => {
          if (!shouldSetQuarantineState) {
            return of(false);
          }

          return this._actionsHttpService.setResourceQuarantineState(actionsHttpParams).pipe(
            map(() => {
              this._notificationService.success(`Resource was ${actionsHttpParams.quarantine_status} successfully`);
              return true;
            }),
            catchError((error: HttpErrorResponse) => {
              this._handleHttpError(
                error,
                `Resource could not be ${actionsHttpParams.quarantine_status}`,
                "Error without 'detail' field"
              );
              return of(false);
            })
          );
        })
      )
      .subscribe(success);

    return success;
  }

  public automationActivation(params: AutomationActivationParams, resourceType: ResourceType): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);
    const resourceTypeStr = resourceType == ResourceType.flow ? 'Flow' : 'Recipe';
    const action = params.action == AutomationActivationActions.start ? 'started' : 'stopped';

    this._actionsHttpService
      .automationActivation(params, resourceType)
      .pipe(
        map(() => {
          this._notificationService.success(`${resourceTypeStr} was ${action} successfully`);
          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          this._handleHttpError(error, `${resourceTypeStr} could not be ${action}`, 'Error not from Zenity API');
          return of(false);
        })
      )
      .subscribe(success);

    return success;
  }

  public getPermissionRequiredForResourceAction(resourceType: ResourceType): PermissionEnum {
    switch (resourceType) {
      case ResourceType.environment:
        return PermissionEnum.admin;
      case ResourceType.flow:
      case ResourceType.canvas_app:
      case ResourceType.custom_connector:
      case ResourceType.connection:
        return PermissionEnum.operator;
      case ResourceType.recipe:
        return PermissionEnum.operator;
      default:
        throw new Error(
          `CommonActionsService getPermissionRequiredForResourceAction: Resource type ${resourceType} is not supported`
        );
    }
  }

  public isGetDefinitionOptionVisible(platform: Platform, resourceType: ResourceType): boolean {
    if (!this._permissionsService.userHasPermission(PERMISSIONS.GLOBAL_OPERATOR)) {
      return false;
    }
    return platform === Platform.powerplatform && this.supportedGetResourceDefinitionTypesArray.includes(resourceType);
  }

  public isGetDefinitionOptionEnabled(integrationId: string): Observable<GetDefinitionEnabledResponse> {
    return this._storeService.integrations$.pipe(
      switchMap((integrations) => {
        for (const integration of integrations) {
          if (integration.id === integrationId) {
            if (getIntegrationStatusLabel(integration.connectionStatus) !== StatusLabelEnum.active) {
              return of({
                enabled: false,
                disabledReason: Label.get('commonActions.getResourceDefinitionAction.inactiveIntegrationMsg'),
              });
            } else {
              return of({
                enabled: true,
                disabledReason: undefined,
              });
            }
          }
        }

        console.error('Could not find integration to determine if get definition action is enabled.');
        return of({ enabled: false, disabledReason: undefined });
      })
    );
  }

  public killAgentAction(resourceName: string): ReplaySubject<boolean> {
    const result = new ReplaySubject<boolean>(1);
    const confirmTitle = `${Label.get('commonActions.killswitchAction.title')}: ${resourceName}`;
    const confirmContent = Label.get('commonActions.killswitchAction.content');

    this._openConfirmationDialog(confirmTitle, confirmContent, Label.get('common.confirm'), Label.get('common.cancel'))
      .pipe(
        filter((confirmed) => confirmed),
        map(() => {
          this._notificationService.success('Agent was contained successfully');
          return true;
        }),
        catchError(() => of(false))
      )
      .subscribe(result);

    return result;
  }

  public findSbomAttachment(attachments: Attachment[]): Attachment | undefined {
    if (!attachments) {
      return undefined;
    }
    return attachments.find((attachment) => attachment.type === AttachmentType.sbom);
  }
}
